import { state } from './state.js';
import {
  switchTab,
  toggleEmbeddingMode,
  fetchHealth,
} from './controllers/app.controller.js';
import {
  fetchConnections,
  testConnectionById,
  handleSaveConnection,
  resetConnectionForm,
  testConnectionForm,
  handleAuthTypeChange,
  renderConnectionsList,
  closeOAuthModal,
  startOAuthFlow,
  closeDeleteModal,
  confirmDeleteConnection,
  openOAuthModal,
  scrollToForm,
  activateConnection,
  editConnection,
  openDeleteModal,
} from './controllers/connections.controller.js';
import {
  switchLogsSubTab,
  toggleLivePolling,
  fetchJobs,
  fetchAuditLogs,
  fetchJobStats,
  createSampleJob,
  exportLogs,
  openClearLogsModal,
  retryAllFailedEntities,
  renderJobsTable,
  closeJobDetailModal,
  cancelJob,
  deleteJob,
  closeClearLogsModal,
  confirmClearLogs,
} from './controllers/logs.controller.js';
import {
  switchMonitoringSubTab,
  toggleMonitoringLivePolling,
  fetchMonitoringData,
  simulateWebhookEvent,
  openPollTriggerModal,
  startRealtimeWorker,
  pauseRealtimeWorker,
  stopRealtimeWorker,
  resetRealtimeWorkerStats,
  startPollingWorker,
  pausePollingWorker,
  stopPollingWorker,
  resetWebhookStats,
  clearWebhookEventsFeed,
  flushWebhookQueue,
  toggleQueuePause,
  retryAllDlq,
  clearWebhookQueueBuffer,
  clearDlq,
  renderWebhookEvents,
  setAllCheckpoints,
  resetAllCheckpoints,
  closeEventDetailModal,
  closePollTriggerModal,
  confirmPollTrigger,
  closeCheckpointEditModal,
  saveCheckpointFromModal,
  executeOnDemandSync,
} from './controllers/monitoring.controller.js';
import {
  switchBatchSubTab,
  fetchBatchInitialData,
  runPreFlightValidation,
  runDryRunDiffPreview,
  executeBatchSyncJob,
  renderCategoryTree,
  selectCategoryFilter,
  fetchCatalogItems,
  selectAllCatalogItems,
  executeSelectiveDryRun,
  executeSelectiveSync,
  clearCatalogSelection,
  setEntityTypePreset,
  filterDiffItems,
} from './controllers/batch.controller.js';
import {
  fetchExplorerItems,
  initExplorer,
  inspectExplorerEntity,
  compareExplorerEntity,
  closeEntityDiffModal,
  executeEntityDiffSync,
  resetExplorerFilters,
  setExplorerEnabledFilter,
  setExplorerEntityType,
  setExplorerPage,
  setExplorerPageSize,
  setExplorerSearch,
  syncExplorerItem,
  executeEntityCascadeSync,
  activeInspectStrategy,
  activeDiffStrategy,
  getSelectedTreeItems,
  // Task 6.2: Entity Deletion & Pruning Controls UI
  deleteExplorerItem,
  toggleExplorerItemSelection,
  selectAllExplorerItems,
  clearExplorerSelection,
  openBatchDeleteModal,
  closeBatchDeleteModal,
  confirmBatchDelete,
  // Task 16.4: Entity Browser UI Integration (Add to Virtual Target Action & Modal)
  openAddToVirtualTargetModal,
  closeAddToVirtualTargetModal,
  confirmAddToVirtualTarget,
  setExplorerTargetMode,
} from './controllers/explorer.controller.js';
import {
  fetchBackupSummary,
  openBackupModal,
  closeBackupModal,
  exportBackup,
  importBackup,
  setBackupScope,
  setBackupFormat,
  setBackupDryRun,
  handleBackupDragOver,
  handleBackupDragLeave,
  handleBackupDrop,
  handleBackupFileSelect,
  preflightRestore,
  confirmRestore,
  resetRestoreState,
} from './controllers/backup.controller.js';
import {
  initComparison,
  setComparisonSide,
  toggleComparisonEntityType,
  selectAllComparisonEntityTypes,
  runComparison,
  loadComparisonReport,
  toggleComparisonTypeExpansion,
  openComparisonEntityDiff,
  deleteComparisonReport,
  renderComparisonDiffModal,
  closeComparisonDiffModal,
  executeComparisonDiffSync,
  openComparisonEntityAddToPackage,
  setComparisonEntityTypeFilter,
  setComparisonEntityTypePage,
  setComparisonEntityTypePageSize,
  setComparisonReportFilter,
  setComparisonEntityTypeChangeFilter,
  setComparisonEntityTypeShowUnchanged,
  isValidComparisonEntityTypeChangeFilter,
} from './controllers/comparison.controller.js';
import {
  initStaging,
  fetchStagingPackages,
  createStagingPackage,
  selectStagingPackage,
  addStagingItem,
  removeStagingItem,
  setStagingCurateField,
  setStagingDeployField,
  switchStagingSubTab,
  runStagingResolve,
  resolveDependencies,
  freezeStagingPackage,
  openEditStagingPackageModal,
  closeEditStagingPackageModal,
  saveEditStagingPackage,
  exportStagingPackage,
  openDeployStagingModal,
  closeDeployStagingModal,
  deployStagingPackage,
  fetchStagingTargetReadiness,
  runStagingDryRunPreview,
  switchStagingDeployView,
  setStagingFilterType,
  setStagingSearch,
  setStagingPage,
  setStagingLimit,
  // Task 16.6: File Import UI in Staging Tab
  handleStagingDragOver,
  handleStagingDragLeave,
  handleStagingDrop,
  handleStagingFileSelect,
  setStagingImportField,
  resetStagingImport,
  importStagingPackageFile,
  // Task 16.8: Staging Entity Detail Inspector & Payload Editor & Bulk Deletion
  inspectStagingItem,
  closeStagingItemInspectModal,
  switchStagingInspectTab,
  setStagingPayloadDraft,
  validateStagingPayloadDraft,
  formatStagingPayloadDraft,
  saveStagingItemPayload,
  deleteStagingItemFromInspector,
  toggleStagingItemSelection,
  selectAllStagingItems,
  clearStagingSelection,
  openStagingBulkDeleteModal,
  closeStagingBulkDeleteModal,
  confirmStagingBulkDelete,
  bulkDeleteStagingItems,
  // Task 16.9: Scenario Presets Wizard (Company Setup, Product Batch, Backup)
  selectScenarioPreset,
  resetScenarioPreset,
  updateScenarioWizardSubmitButton,
  setStagingScenarioFilter,
} from './controllers/staging.controller.js';
import { StagingScenario } from './types.js';
import { showToast } from './utils/toast.js';

/**
 * Initialize all DOM Event Listeners
 */
export function initEvents(): void {
  // Tab navbar clicks
  document.querySelectorAll('.tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.getAttribute('data-tab');
      if (tab) switchTab(tab);
    });
  });

  // Module cards navigation
  document.querySelectorAll('[data-nav]').forEach((card) => {
    card.addEventListener('click', () => {
      const targetNav = card.getAttribute('data-nav');
      if (targetNav) switchTab(targetNav);
    });
  });

  // Action buttons on Overview
  const btnQuickSync = document.getElementById('btn-quick-sync');
  if (btnQuickSync) {
    btnQuickSync.addEventListener('click', () => switchTab('batch'));
  }

  const btnManageConnections = document.getElementById('btn-manage-connections');
  if (btnManageConnections) {
    btnManageConnections.addEventListener('click', () => switchTab('connections'));
  }

  const btnRefresh = document.getElementById('btn-refresh-health');
  if (btnRefresh) {
    btnRefresh.addEventListener('click', async () => {
      btnRefresh.style.transform = 'rotate(180deg)';
      await fetchHealth();
      await fetchConnections();
      setTimeout(() => {
        btnRefresh.style.transform = 'none';
      }, 300);
      showToast('Status refreshed', 'success', 2000);
    });
  }

  // Connections Tab Actions
  const btnNewConn = document.getElementById('btn-new-connection');
  if (btnNewConn) {
    btnNewConn.addEventListener('click', scrollToForm);
  }

  const btnRefreshConns = document.getElementById('btn-refresh-connections');
  if (btnRefreshConns) {
    btnRefreshConns.addEventListener('click', async () => {
      await fetchConnections();
      showToast('Connections reloaded', 'success', 1500);
    });
  }

  const btnOAuthQuick = document.getElementById('btn-oauth-quick-connect');
  if (btnOAuthQuick) {
    btnOAuthQuick.addEventListener('click', openOAuthModal);
  }

  const btnBackupModal = document.getElementById('btn-backup-modal');
  if (btnBackupModal) {
    btnBackupModal.addEventListener('click', openBackupModal);
  }

  const btnGlobalBackup = document.getElementById('btn-global-backup-modal');
  if (btnGlobalBackup) {
    btnGlobalBackup.addEventListener('click', openBackupModal);
  }

  const btnHeroBackup = document.getElementById('btn-hero-backup-modal');
  if (btnHeroBackup) {
    btnHeroBackup.addEventListener('click', openBackupModal);
  }

  const btnTestBoth = document.getElementById('btn-test-active-both');
  if (btnTestBoth) {
    btnTestBoth.addEventListener('click', async () => {
      const { source, target } = state.connections;
      if (!source && !target) {
        showToast('No active source or target configured', 'warning');
        return;
      }
      if (source) await testConnectionById(source.id);
      if (target) await testConnectionById(target.id);
    });
  }

  const connectionsGrid = document.getElementById('connections-list-grid');
  if (connectionsGrid) {
    connectionsGrid.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('button[data-action]') as HTMLElement | null;
      if (!btn) return;

      const action = btn.getAttribute('data-action');
      const id = btn.getAttribute('data-id');
      const name = btn.getAttribute('data-name');
      if (!id) return;

      if (action === 'test') {
        testConnectionById(id, btn);
      } else if (action === 'activate') {
        activateConnection(id);
      } else if (action === 'edit') {
        editConnection(id);
      } else if (action === 'delete') {
        openDeleteModal(id, name || '');
      }
    });
  }

  // Connection Form Events
  const connForm = document.getElementById('connection-form');
  if (connForm) {
    connForm.addEventListener('submit', handleSaveConnection);
  }

  const btnCancelEdit = document.getElementById('btn-cancel-conn-edit');
  if (btnCancelEdit) {
    btnCancelEdit.addEventListener('click', resetConnectionForm);
  }

  const btnTestForm = document.getElementById('btn-test-form-conn');
  if (btnTestForm) {
    btnTestForm.addEventListener('click', testConnectionForm);
  }

  const authTypeSelect = document.getElementById('conn-auth-type');
  if (authTypeSelect) {
    authTypeSelect.addEventListener('change', handleAuthTypeChange);
  }

  // Password / Secret toggles
  const btnToggleSecret = document.getElementById('btn-toggle-secret');
  if (btnToggleSecret) {
    btnToggleSecret.addEventListener('click', () => {
      const input = document.getElementById('conn-client-secret') as HTMLInputElement | null;
      if (input) {
        input.type = input.type === 'password' ? 'text' : 'password';
      }
    });
  }

  const btnTogglePassword = document.getElementById('btn-toggle-password');
  if (btnTogglePassword) {
    btnTogglePassword.addEventListener('click', () => {
      const input = document.getElementById('conn-password') as HTMLInputElement | null;
      if (input) {
        input.type = input.type === 'password' ? 'text' : 'password';
      }
    });
  }

  const btnToggleToken = document.getElementById('btn-toggle-token');
  if (btnToggleToken) {
    btnToggleToken.addEventListener('click', () => {
      const input = document.getElementById('conn-access-token') as HTMLInputElement | null;
      if (input) {
        input.type = input.type === 'password' ? 'text' : 'password';
      }
    });
  }

  // Inventory Filter Tabs
  document.querySelectorAll('.filter-tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.filter-tab-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      state.connections.filter = (btn.getAttribute('data-filter') as any) || 'all';
      renderConnectionsList();
    });
  });

  // Search Input
  const searchInput = document.getElementById('conn-search-input') as HTMLInputElement | null;
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      state.connections.search = (e.target as HTMLInputElement).value;
      renderConnectionsList();
    });
  }

  // Modals Events
  const btnCloseOAuth = document.getElementById('btn-close-oauth-modal');
  const btnCancelOAuth = document.getElementById('btn-cancel-oauth-modal');
  const btnStartOAuth = document.getElementById('btn-start-oauth-flow');
  if (btnCloseOAuth) btnCloseOAuth.addEventListener('click', closeOAuthModal);
  if (btnCancelOAuth) btnCancelOAuth.addEventListener('click', closeOAuthModal);
  if (btnStartOAuth) btnStartOAuth.addEventListener('click', startOAuthFlow);

  const btnCloseDelete = document.getElementById('btn-close-delete-modal');
  const btnCancelDelete = document.getElementById('btn-cancel-delete-modal');
  const btnConfirmDelete = document.getElementById('btn-confirm-delete');
  if (btnCloseDelete) btnCloseDelete.addEventListener('click', closeDeleteModal);
  if (btnCancelDelete) btnCancelDelete.addEventListener('click', closeDeleteModal);
  if (btnConfirmDelete) btnConfirmDelete.addEventListener('click', confirmDeleteConnection);

  // Backup Modal Events
  const btnCloseBackup = document.getElementById('btn-close-backup-modal');
  const btnCancelBackup = document.getElementById('btn-cancel-backup-modal');
  const btnExportBackup = document.getElementById('btn-export-backup');
  const btnImportBackup = document.getElementById('btn-import-backup');
  const btnPreflightRestore = document.getElementById('btn-preflight-restore');
  const btnConfirmRestore = document.getElementById('btn-confirm-restore');
  const dropZone = document.getElementById('backup-restore-dropzone');
  const restoreFileInput = document.getElementById('backup-restore-file-input') as HTMLInputElement | null;
  const dryRunCheckbox = document.getElementById('backup-restore-dry-run') as HTMLInputElement | null;

  if (btnCloseBackup) btnCloseBackup.addEventListener('click', closeBackupModal);
  if (btnCancelBackup) btnCancelBackup.addEventListener('click', closeBackupModal);
  if (btnExportBackup) btnExportBackup.addEventListener('click', exportBackup);
  if (btnImportBackup) btnImportBackup.addEventListener('click', importBackup);
  if (btnPreflightRestore) btnPreflightRestore.addEventListener('click', preflightRestore);
  if (btnConfirmRestore) btnConfirmRestore.addEventListener('click', confirmRestore);

  if (dropZone) {
    dropZone.addEventListener('dragover', handleBackupDragOver);
    dropZone.addEventListener('dragleave', handleBackupDragLeave);
    dropZone.addEventListener('drop', handleBackupDrop);
    dropZone.addEventListener('click', () => restoreFileInput?.click());
  }

  if (restoreFileInput) {
    restoreFileInput.addEventListener('change', handleBackupFileSelect);
  }

  if (dryRunCheckbox) {
    dryRunCheckbox.addEventListener('change', () => setBackupDryRun(dryRunCheckbox.checked));
  }

  document.querySelectorAll('input[name="backup-export-scope"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      const value = (radio as HTMLInputElement).value;
      if (value === 'setup' || value === 'catalog' || value === 'users' || value === 'full') {
        setBackupScope(value);
      }
    });
  });

  document.querySelectorAll('input[name="backup-export-format"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      const value = (radio as HTMLInputElement).value;
      if (value === 'archive' || value === 'directory') {
        setBackupFormat(value);
      }
    });
  });

  // ==========================================================================
  // Task 6.3: Staging & Release Package Builder UI Event Listeners
  // ==========================================================================
  const stagingPackagesList = document.getElementById('staging-packages-list');
  if (stagingPackagesList) {
    stagingPackagesList.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const selectBtn = target.closest('.btn-staging-select') as HTMLElement | null;
      const exportBtn = target.closest('.btn-staging-export') as HTMLElement | null;
      const card = target.closest('.staging-package-card') as HTMLElement | null;
      if (selectBtn) {
        const id = selectBtn.getAttribute('data-id');
        if (id) selectStagingPackage(id);
      } else if (exportBtn) {
        const id = exportBtn.getAttribute('data-id');
        if (id) selectStagingPackage(id).then(() => exportStagingPackage());
      } else if (card && !target.closest('button')) {
        const id = card.getAttribute('data-id');
        if (id) selectStagingPackage(id);
      }
    });
  }

  document.querySelectorAll('#staging-view-container .subtab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const subtab = btn.getAttribute('data-subtab');
      if (subtab && (subtab === 'packages' || subtab === 'curate' || subtab === 'release')) {
        switchStagingSubTab(subtab);
      }
    });
  });

  // Task 16.9: Scenario Preset Cards in Staging Wizard
  const stagingScenarioCards = document.getElementById('staging-scenario-cards');
  if (stagingScenarioCards) {
    stagingScenarioCards.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const card = target.closest('.scenario-preset-card') as HTMLElement | null;
      if (card) {
        const scenario = card.getAttribute('data-scenario') as StagingScenario;
        if (scenario) selectScenarioPreset(scenario);
      }
    });

    stagingScenarioCards.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const target = e.target as HTMLElement;
        const card = target.closest('.scenario-preset-card') as HTMLElement | null;
        if (card) {
          e.preventDefault();
          const scenario = card.getAttribute('data-scenario') as StagingScenario;
          if (scenario) selectScenarioPreset(scenario);
        }
      }
    });
  }

  // Reset Preset button
  const btnResetScenarioPreset = document.getElementById('btn-reset-scenario-preset');
  if (btnResetScenarioPreset) {
    btnResetScenarioPreset.addEventListener('click', () => {
      resetScenarioPreset();
    });
  }

  // Dynamic submit button updater on auto-fetch checkbox toggle
  ['staging-wizard-company-auto-fetch', 'staging-wizard-product-auto-fetch', 'staging-wizard-backup-auto-fetch'].forEach((id) => {
    const cb = document.getElementById(id);
    if (cb) {
      cb.addEventListener('change', () => {
        updateScenarioWizardSubmitButton();
      });
    }
  });

  const stagingNewPackageForm = document.getElementById('staging-new-package-form');
  if (stagingNewPackageForm) {
    stagingNewPackageForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const nameInput = document.getElementById('staging-package-name') as HTMLInputElement | null;
      const descInput = document.getElementById('staging-package-description') as HTMLInputElement | null;
      const scenarioInput = document.getElementById('staging-package-scenario') as HTMLInputElement | null;
      const name = nameInput?.value || '';
      const description = descInput?.value || '';
      const scenario = (scenarioInput?.value || state.staging.scenarioWizard.selectedScenario || 'company_setup') as StagingScenario;

      let options: Record<string, unknown> = {};
      if (scenario === 'company_setup') {
        const localesCb = document.getElementById('staging-wizard-company-locales') as HTMLInputElement | null;
        const channelsCb = document.getElementById('staging-wizard-company-channels') as HTMLInputElement | null;
        const attrsCb = document.getElementById('staging-wizard-company-attributes') as HTMLInputElement | null;
        const familiesCb = document.getElementById('staging-wizard-company-families') as HTMLInputElement | null;
        const categoriesCb = document.getElementById('staging-wizard-company-categories') as HTMLInputElement | null;
        const refEntitiesCb = document.getElementById('staging-wizard-company-ref-entities') as HTMLInputElement | null;
        const autoFetchCb = document.getElementById('staging-wizard-company-auto-fetch') as HTMLInputElement | null;

        options = {
          includeLocales: localesCb ? localesCb.checked : true,
          includeChannels: channelsCb ? channelsCb.checked : true,
          includeAttributes: attrsCb ? attrsCb.checked : true,
          includeFamilies: familiesCb ? familiesCb.checked : true,
          includeCategories: categoriesCb ? categoriesCb.checked : true,
          includeReferenceEntities: refEntitiesCb ? refEntitiesCb.checked : true,
          autoFetchSource: autoFetchCb ? autoFetchCb.checked : false,
        };
      } else if (scenario === 'product_batch') {
        const familySel = document.getElementById('staging-wizard-product-family') as HTMLSelectElement | null;
        const categorySel = document.getElementById('staging-wizard-product-category') as HTMLSelectElement | null;
        const limitInput = document.getElementById('staging-wizard-product-limit') as HTMLInputElement | null;
        const resolveDepsCb = document.getElementById('staging-wizard-product-resolve-deps') as HTMLInputElement | null;
        const includeMediaCb = document.getElementById('staging-wizard-product-include-media') as HTMLInputElement | null;
        const autoFetchCb = document.getElementById('staging-wizard-product-auto-fetch') as HTMLInputElement | null;

        options = {
          family: familySel?.value || '',
          category: categorySel?.value || '',
          limit: limitInput ? (parseInt(limitInput.value, 10) || 50) : 50,
          resolveDependencies: resolveDepsCb ? resolveDepsCb.checked : true,
          includeMedia: includeMediaCb ? includeMediaCb.checked : true,
          autoFetchSource: autoFetchCb ? autoFetchCb.checked : false,
        };
      } else if (scenario === 'backup_snapshot') {
        const scopeSel = document.getElementById('staging-wizard-backup-scope') as HTMLSelectElement | null;
        const mediaCb = document.getElementById('staging-wizard-backup-media') as HTMLInputElement | null;
        const freezeCb = document.getElementById('staging-wizard-backup-freeze') as HTMLInputElement | null;
        const autoFetchCb = document.getElementById('staging-wizard-backup-auto-fetch') as HTMLInputElement | null;

        options = {
          scope: scopeSel?.value || 'full',
          includeMedia: mediaCb ? mediaCb.checked : true,
          autoFreeze: freezeCb ? freezeCb.checked : false,
          autoFetchSource: autoFetchCb ? autoFetchCb.checked : false,
        };
      }

      const pkg = await createStagingPackage(name, description, scenario, options);
      if (pkg) {
        resetScenarioPreset();
      }
    });
  }

  const stagingPackageNameInput = document.getElementById('staging-package-name') as HTMLInputElement | null;
  if (stagingPackageNameInput) {
    stagingPackageNameInput.addEventListener('input', (e) => {
      setStagingCurateField('newPackageName', (e.target as HTMLInputElement).value);
    });
  }

  const stagingPackageDescInput = document.getElementById('staging-package-description') as HTMLInputElement | null;
  if (stagingPackageDescInput) {
    stagingPackageDescInput.addEventListener('input', (e) => {
      setStagingCurateField('newPackageDescription', (e.target as HTMLInputElement).value);
    });
  }

  const stagingEntityTypeSelect = document.getElementById('staging-entity-type') as HTMLSelectElement | null;
  if (stagingEntityTypeSelect) {
    stagingEntityTypeSelect.addEventListener('change', () => {
      setStagingCurateField('entityType', stagingEntityTypeSelect.value);
    });
  }

  const stagingEntityIdInput = document.getElementById('staging-entity-id') as HTMLInputElement | null;
  if (stagingEntityIdInput) {
    stagingEntityIdInput.addEventListener('input', (e) => {
      setStagingCurateField('entityId', (e.target as HTMLInputElement).value);
    });
  }

  const stagingAddItemForm = document.getElementById('staging-add-item-form');
  if (stagingAddItemForm) {
    stagingAddItemForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const typeSelect = document.getElementById('staging-entity-type') as HTMLSelectElement | null;
      const idInput = document.getElementById('staging-entity-id') as HTMLInputElement | null;
      const type = typeSelect?.value || 'products';
      const id = idInput?.value || '';
      await addStagingItem(type, id);
      if (idInput) idInput.value = '';
      setStagingCurateField('entityId', '');
    });
  }

  const stagingItemsList = document.getElementById('staging-items-list');
  if (stagingItemsList) {
    stagingItemsList.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const removeBtn = target.closest('.btn-staging-remove') as HTMLElement | null;
      if (removeBtn) {
        const id = removeBtn.getAttribute('data-id');
        if (id) removeStagingItem(id);
      }
    });
  }

  // Staging Browser: Category Filter Tabs
  const stagingCategoryTabs = document.getElementById('staging-category-tabs');
  if (stagingCategoryTabs) {
    stagingCategoryTabs.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const tabBtn = target.closest('.staging-category-tab') as HTMLElement | null;
      if (tabBtn) {
        const type = tabBtn.getAttribute('data-entity-type');
        if (type) setStagingFilterType(type);
      }
    });
  }

  // Staging Browser: Search Input
  const stagingSearchInput = document.getElementById('staging-search-input') as HTMLInputElement | null;
  if (stagingSearchInput) {
    stagingSearchInput.addEventListener('input', (e) => {
      setStagingSearch((e.target as HTMLInputElement).value);
    });
  }

  // Staging Browser: Clear Search Button
  const btnStagingSearchClear = document.getElementById('btn-staging-search-clear');
  if (btnStagingSearchClear) {
    btnStagingSearchClear.addEventListener('click', () => {
      if (stagingSearchInput) stagingSearchInput.value = '';
      setStagingSearch('');
    });
  }

  // Staging Browser: Page Size Select
  const stagingPageSize = document.getElementById('staging-page-size') as HTMLSelectElement | null;
  if (stagingPageSize) {
    stagingPageSize.addEventListener('change', () => {
      const limit = Number(stagingPageSize.value);
      if (limit) setStagingLimit(limit);
    });
  }

  // Staging Browser: Prev & Next Pagination Buttons
  const stagingBtnPrev = document.getElementById('staging-btn-prev');
  if (stagingBtnPrev) {
    stagingBtnPrev.addEventListener('click', () => {
      const { state } = window.AkeneoDashboard || {};
      const curPage = state?.staging?.page || 1;
      setStagingPage(curPage - 1);
    });
  }

  const stagingBtnNext = document.getElementById('staging-btn-next');
  if (stagingBtnNext) {
    stagingBtnNext.addEventListener('click', () => {
      const { state } = window.AkeneoDashboard || {};
      const curPage = state?.staging?.page || 1;
      setStagingPage(curPage + 1);
    });
  }

  const btnStagingResolve = document.getElementById('btn-staging-resolve');
  if (btnStagingResolve) {
    btnStagingResolve.addEventListener('click', () => runStagingResolve('full'));
  }

  const btnStagingResolveLean = document.getElementById('btn-staging-resolve-lean');
  if (btnStagingResolveLean) {
    btnStagingResolveLean.addEventListener('click', () => runStagingResolve('lean'));
  }

  const btnStagingResolveDepsCurate = document.getElementById('btn-staging-resolve-deps-curate');
  if (btnStagingResolveDepsCurate) {
    btnStagingResolveDepsCurate.addEventListener('click', () => {
      const packageId = state.staging.selectedPackageId;
      if (packageId) {
        resolveDependencies(packageId);
      } else {
        showToast('Select a package to resolve dependencies', 'warning');
      }
    });
  }

  const btnStagingFreeze = document.getElementById('btn-staging-freeze');
  if (btnStagingFreeze) {
    btnStagingFreeze.addEventListener('click', () => freezeStagingPackage());
  }

  const btnStagingExport = document.getElementById('btn-staging-export');
  if (btnStagingExport) {
    btnStagingExport.addEventListener('click', () => exportStagingPackage());
  }

  const btnStagingDeploy = document.getElementById('btn-staging-deploy');
  if (btnStagingDeploy) {
    btnStagingDeploy.addEventListener('click', () => openDeployStagingModal());
  }

  const btnStagingRefresh = document.getElementById('btn-staging-refresh');
  if (btnStagingRefresh) {
    btnStagingRefresh.addEventListener('click', async () => {
      await fetchStagingPackages();
      showToast('Staging packages refreshed', 'success', 1500);
    });
  }

  // Task 16.9: Scenario filter tabs in Staging packages list
  const stagingScenarioFilters = document.getElementById('staging-package-scenario-filters');
  if (stagingScenarioFilters) {
    stagingScenarioFilters.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('.filter-tab-btn') as HTMLElement | null;
      if (btn) {
        const scenarioFilter = btn.getAttribute('data-scenario-filter') || 'all';
        setStagingScenarioFilter(scenarioFilter);
      }
    });
  }

  // Edit package metadata modal event bindings
  const btnCloseEditPackage = document.getElementById('btn-close-staging-edit-package-modal');
  if (btnCloseEditPackage) btnCloseEditPackage.addEventListener('click', closeEditStagingPackageModal);

  const btnCancelEditPackage = document.getElementById('btn-cancel-staging-edit-package-modal');
  if (btnCancelEditPackage) btnCancelEditPackage.addEventListener('click', closeEditStagingPackageModal);

  const btnConfirmEditPackage = document.getElementById('btn-confirm-staging-edit-package-modal');
  if (btnConfirmEditPackage) btnConfirmEditPackage.addEventListener('click', saveEditStagingPackage);

  const stagingDeployTarget = document.getElementById('staging-deploy-target') as HTMLSelectElement | null;
  if (stagingDeployTarget) {
    stagingDeployTarget.addEventListener('change', () => {
      setStagingDeployField('targetConnectionId', stagingDeployTarget.value);
    });
  }

  const stagingDeployDryRun = document.getElementById('staging-deploy-dry-run') as HTMLInputElement | null;
  if (stagingDeployDryRun) {
    stagingDeployDryRun.addEventListener('change', () => {
      setStagingDeployField('dryRun', stagingDeployDryRun.checked);
    });
  }

  const btnCloseDeployStaging = document.getElementById('btn-close-staging-deploy-modal');
  const btnCancelDeployStaging = document.getElementById('btn-cancel-staging-deploy-modal');
  const btnConfirmDeployStaging = document.getElementById('btn-confirm-staging-deploy-modal');
  const btnCheckReadiness = document.getElementById('btn-staging-deploy-check-readiness');
  const btnDryRunPreview = document.getElementById('btn-staging-deploy-dryrun-preview');
  const btnBackDeploy = document.getElementById('btn-back-staging-deploy-modal');
  const btnDoneDeploy = document.getElementById('btn-done-staging-deploy-modal');
  const btnViewLogsDeploy = document.getElementById('btn-staging-deploy-view-logs');
  const btnViewFailuresDeploy = document.getElementById('btn-staging-deploy-view-failures');

  if (btnCloseDeployStaging) btnCloseDeployStaging.addEventListener('click', closeDeployStagingModal);
  if (btnCancelDeployStaging) btnCancelDeployStaging.addEventListener('click', closeDeployStagingModal);
  if (btnDoneDeploy) btnDoneDeploy.addEventListener('click', closeDeployStagingModal);
  if (btnConfirmDeployStaging) btnConfirmDeployStaging.addEventListener('click', () => deployStagingPackage());
  if (btnCheckReadiness) btnCheckReadiness.addEventListener('click', () => fetchStagingTargetReadiness());
  if (btnDryRunPreview) btnDryRunPreview.addEventListener('click', () => runStagingDryRunPreview());
  if (btnBackDeploy) btnBackDeploy.addEventListener('click', () => switchStagingDeployView('config'));
  if (btnViewLogsDeploy) {
    btnViewLogsDeploy.addEventListener('click', () => {
      closeDeployStagingModal();
      switchTab('logs');
    });
  }
  if (btnViewFailuresDeploy) {
    btnViewFailuresDeploy.addEventListener('click', () => {
      closeDeployStagingModal();
      switchTab('logs');
    });
  }

  // Task 16.6: File Import UI in Staging Tab
  const stagingDropzone = document.getElementById('staging-import-dropzone');
  const stagingFileInput = document.getElementById('staging-import-file-input') as HTMLInputElement | null;
  if (stagingDropzone) {
    stagingDropzone.addEventListener('click', () => {
      if (stagingFileInput) stagingFileInput.click();
    });
    stagingDropzone.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (stagingFileInput) stagingFileInput.click();
      }
    });
    stagingDropzone.addEventListener('dragover', (e) => handleStagingDragOver(e as DragEvent));
    stagingDropzone.addEventListener('dragleave', (e) => handleStagingDragLeave(e as DragEvent));
    stagingDropzone.addEventListener('drop', (e) => handleStagingDrop(e as DragEvent));
  }

  if (stagingFileInput) {
    stagingFileInput.addEventListener('change', (e) => handleStagingFileSelect(e));
  }

  const stagingImportTarget = document.getElementById('staging-import-target-select') as HTMLSelectElement | null;
  if (stagingImportTarget) {
    stagingImportTarget.addEventListener('change', () => {
      setStagingImportField('targetPackageId', stagingImportTarget.value);
    });
  }

  const stagingImportMode = document.getElementById('staging-import-mode-select') as HTMLSelectElement | null;
  if (stagingImportMode) {
    stagingImportMode.addEventListener('change', () => {
      setStagingImportField('mode', stagingImportMode.value);
    });
  }

  const stagingImportScenario = document.getElementById('staging-import-scenario-select') as HTMLSelectElement | null;
  if (stagingImportScenario) {
    stagingImportScenario.addEventListener('change', () => {
      setStagingImportField('scenario', stagingImportScenario.value);
    });
  }

  const btnStagingImportSubmit = document.getElementById('btn-staging-import-submit');
  if (btnStagingImportSubmit) {
    btnStagingImportSubmit.addEventListener('click', () => importStagingPackageFile());
  }

  const btnStagingImportClear = document.getElementById('btn-staging-import-clear');
  if (btnStagingImportClear) {
    btnStagingImportClear.addEventListener('click', () => resetStagingImport());
  }

  // ==========================================================================
  // Task 16.8: Staging Entity Detail Inspector & Payload Editor Event Listeners
  // ==========================================================================
  const btnCloseStagingInspect = document.getElementById('btn-close-staging-item-inspect');
  const btnDismissStagingInspect = document.getElementById('btn-dismiss-staging-item-inspect');
  if (btnCloseStagingInspect) btnCloseStagingInspect.addEventListener('click', closeStagingItemInspectModal);
  if (btnDismissStagingInspect) btnDismissStagingInspect.addEventListener('click', closeStagingItemInspectModal);

  document.querySelectorAll('#staging-item-inspect-modal .filter-tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.getAttribute('data-inspect-tab');
      if (tab === 'values' || tab === 'editor' || tab === 'deps') {
        switchStagingInspectTab(tab);
      }
    });
  });

  const inspectPayloadTextarea = document.getElementById('staging-item-payload-editor') as HTMLTextAreaElement | null;
  if (inspectPayloadTextarea) {
    inspectPayloadTextarea.addEventListener('input', (e) => {
      setStagingPayloadDraft((e.target as HTMLTextAreaElement).value);
    });
  }

  const btnFormatPayload = document.getElementById('btn-staging-format-payload');
  if (btnFormatPayload) {
    btnFormatPayload.addEventListener('click', () => formatStagingPayloadDraft());
  }

  const btnCopyPayload = document.getElementById('btn-staging-copy-payload');
  if (btnCopyPayload) {
    btnCopyPayload.addEventListener('click', () => {
      const draft = state.staging.inspectState.payloadDraft || inspectPayloadTextarea?.value || '';
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(draft);
      }
      showToast('JSON copied to clipboard', 'info', 1500);
    });
  }

  const btnSavePayload = document.getElementById('btn-staging-save-payload');
  if (btnSavePayload) {
    btnSavePayload.addEventListener('click', () => saveStagingItemPayload());
  }

  const btnInspectDelete = document.getElementById('btn-staging-inspect-delete');
  if (btnInspectDelete) {
    btnInspectDelete.addEventListener('click', () => deleteStagingItemFromInspector());
  }

  // Bulk Actions & Bulk Delete Modal Listeners
  const selectAllStagingCb = document.getElementById('staging-select-all-checkbox') as HTMLInputElement | null;
  if (selectAllStagingCb) {
    selectAllStagingCb.addEventListener('change', (e) => {
      selectAllStagingItems((e.target as HTMLInputElement).checked);
    });
  }

  const btnStagingSelectAll = document.getElementById('btn-staging-select-all');
  if (btnStagingSelectAll) {
    btnStagingSelectAll.addEventListener('click', () => selectAllStagingItems(true));
  }

  const btnStagingClearSelection = document.getElementById('btn-staging-clear-selection');
  if (btnStagingClearSelection) {
    btnStagingClearSelection.addEventListener('click', () => clearStagingSelection());
  }

  const btnStagingBulkDelete = document.getElementById('btn-staging-bulk-delete');
  if (btnStagingBulkDelete) {
    btnStagingBulkDelete.addEventListener('click', () => openStagingBulkDeleteModal());
  }

  const btnCloseBulkDelete = document.getElementById('btn-close-staging-bulk-delete');
  const btnCancelBulkDelete = document.getElementById('btn-cancel-staging-bulk-delete');
  const btnConfirmBulkDelete = document.getElementById('btn-confirm-staging-bulk-delete');

  if (btnCloseBulkDelete) btnCloseBulkDelete.addEventListener('click', closeStagingBulkDeleteModal);
  if (btnCancelBulkDelete) btnCancelBulkDelete.addEventListener('click', closeStagingBulkDeleteModal);
  if (btnConfirmBulkDelete) btnConfirmBulkDelete.addEventListener('click', () => confirmStagingBulkDelete());

  // ==========================================================================
  // Task 9.4: Sync History & Audit Logs Event Listeners
  // ==========================================================================

  // Subtab navigation in Logs panel
  document.querySelectorAll('.subtab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const subtab = btn.getAttribute('data-subtab');
      if (subtab && (subtab === 'jobs' || subtab === 'stream' || subtab === 'errors')) {
        switchLogsSubTab(subtab);
      }
    });
  });

  // Top action buttons in Logs panel
  const btnLiveToggle = document.getElementById('btn-live-toggle');
  if (btnLiveToggle) {
    btnLiveToggle.addEventListener('click', toggleLivePolling);
  }

  const btnRefreshLogs = document.getElementById('btn-refresh-logs');
  if (btnRefreshLogs) {
    btnRefreshLogs.addEventListener('click', async () => {
      await fetchJobs();
      await fetchAuditLogs();
      await fetchJobStats();
      showToast('Jobs & Logs refreshed', 'success', 1500);
    });
  }

  const btnCreateSample = document.getElementById('btn-create-sample-job');
  if (btnCreateSample) {
    btnCreateSample.addEventListener('click', createSampleJob);
  }

  const btnEmptySample = document.getElementById('btn-empty-sample-job');
  if (btnEmptySample) {
    btnEmptySample.addEventListener('click', createSampleJob);
  }

  const btnExportLogs = document.getElementById('btn-export-logs');
  if (btnExportLogs) {
    btnExportLogs.addEventListener('click', () => exportLogs('json'));
  }

  const btnOpenClearLogs = document.getElementById('btn-open-clear-logs');
  if (btnOpenClearLogs) {
    btnOpenClearLogs.addEventListener('click', openClearLogsModal);
  }

  const btnRetryAllDiag = document.getElementById('btn-retry-all-diagnostics');
  if (btnRetryAllDiag) {
    btnRetryAllDiag.addEventListener('click', retryAllFailedEntities);
  }

  // Jobs Filter & Search
  const jobSearchInput = document.getElementById('job-search-input') as HTMLInputElement | null;
  if (jobSearchInput) {
    jobSearchInput.addEventListener('input', (e) => {
      state.jobs.search = (e.target as HTMLInputElement).value;
      renderJobsTable();
    });
  }

  const jobFilterStatus = document.getElementById('job-filter-status') as HTMLSelectElement | null;
  if (jobFilterStatus) {
    jobFilterStatus.addEventListener('change', (e) => {
      state.jobs.filterStatus = (e.target as HTMLSelectElement).value;
      state.jobs.page = 1;
      fetchJobs();
    });
  }

  const jobFilterType = document.getElementById('job-filter-type') as HTMLSelectElement | null;
  if (jobFilterType) {
    jobFilterType.addEventListener('change', (e) => {
      state.jobs.filterType = (e.target as HTMLSelectElement).value;
      state.jobs.page = 1;
      fetchJobs();
    });
  }

  const btnResetJobFilters = document.getElementById('btn-reset-job-filters');
  if (btnResetJobFilters) {
    btnResetJobFilters.addEventListener('click', () => {
      state.jobs.search = '';
      state.jobs.filterStatus = '';
      state.jobs.filterType = '';
      state.jobs.page = 1;
      if (jobSearchInput) jobSearchInput.value = '';
      if (jobFilterStatus) jobFilterStatus.value = '';
      if (jobFilterType) jobFilterType.value = '';
      fetchJobs();
    });
  }

  // Audit Logs Filter & Search
  const logSearchInput = document.getElementById('log-search-input') as HTMLInputElement | null;
  if (logSearchInput) {
    logSearchInput.addEventListener('input', (e) => {
      state.logs.search = (e.target as HTMLInputElement).value;
      fetchAuditLogs();
    });
  }

  const logFilterLevel = document.getElementById('log-filter-level') as HTMLSelectElement | null;
  if (logFilterLevel) {
    logFilterLevel.addEventListener('change', (e) => {
      state.logs.filterLevel = (e.target as HTMLSelectElement).value;
      document.querySelectorAll('.log-chip-btn').forEach((chip) => {
        chip.classList.toggle('active', (chip.getAttribute('data-level') || '') === (e.target as HTMLSelectElement).value);
      });
      fetchAuditLogs();
    });
  }

  const logFilterCategory = document.getElementById('log-filter-category') as HTMLSelectElement | null;
  if (logFilterCategory) {
    logFilterCategory.addEventListener('change', (e) => {
      state.logs.filterCategory = (e.target as HTMLSelectElement).value;
      fetchAuditLogs();
    });
  }

  const btnResetLogFilters = document.getElementById('btn-reset-log-filters');
  if (btnResetLogFilters) {
    btnResetLogFilters.addEventListener('click', () => {
      state.logs.search = '';
      state.logs.filterLevel = '';
      state.logs.filterCategory = '';
      if (logSearchInput) logSearchInput.value = '';
      if (logFilterLevel) logFilterLevel.value = '';
      if (logFilterCategory) logFilterCategory.value = '';
      document.querySelectorAll('.log-chip-btn').forEach((chip) => {
        chip.classList.toggle('active', (chip.getAttribute('data-level') || '') === '');
      });
      fetchAuditLogs();
    });
  }

  // Level Chip buttons
  document.querySelectorAll('.log-chip-btn').forEach((chip) => {
    chip.addEventListener('click', () => {
      document.querySelectorAll('.log-chip-btn').forEach((c) => c.classList.remove('active'));
      chip.classList.add('active');
      const lvl = chip.getAttribute('data-level') || '';
      state.logs.filterLevel = lvl;
      if (logFilterLevel) logFilterLevel.value = lvl;
      fetchAuditLogs();
    });
  });

  // Stream viewer scroll controls
  const btnStreamScrollTop = document.getElementById('btn-stream-scroll-top');
  if (btnStreamScrollTop) {
    btnStreamScrollTop.addEventListener('click', () => {
      const viewer = document.getElementById('audit-stream-viewer');
      if (viewer) viewer.scrollTop = 0;
    });
  }

  const btnStreamScrollBottom = document.getElementById('btn-stream-scroll-bottom');
  if (btnStreamScrollBottom) {
    btnStreamScrollBottom.addEventListener('click', () => {
      const viewer = document.getElementById('audit-stream-viewer');
      if (viewer) viewer.scrollTop = viewer.scrollHeight;
    });
  }

  // Job Modal Actions
  const btnCloseJobModal = document.getElementById('btn-close-job-modal');
  if (btnCloseJobModal) btnCloseJobModal.addEventListener('click', closeJobDetailModal);

  const btnDismissJobModal = document.getElementById('btn-dismiss-job-modal');
  if (btnDismissJobModal) btnDismissJobModal.addEventListener('click', closeJobDetailModal);

  const btnModalCancelJob = document.getElementById('btn-modal-cancel-job');
  if (btnModalCancelJob) {
    btnModalCancelJob.addEventListener('click', () => {
      if (state.jobs.selectedJobId) cancelJob(state.jobs.selectedJobId);
    });
  }

  const btnModalDeleteJob = document.getElementById('btn-modal-delete-job');
  if (btnModalDeleteJob) {
    btnModalDeleteJob.addEventListener('click', () => {
      if (state.jobs.selectedJobId) deleteJob(state.jobs.selectedJobId);
    });
  }

  // Clear Logs Modal Actions
  const btnCloseClearLogs = document.getElementById('btn-close-clear-logs');
  if (btnCloseClearLogs) btnCloseClearLogs.addEventListener('click', closeClearLogsModal);

  const btnCancelClearLogs = document.getElementById('btn-cancel-clear-logs');
  if (btnCancelClearLogs) btnCancelClearLogs.addEventListener('click', closeClearLogsModal);

  const btnConfirmClearLogs = document.getElementById('btn-confirm-clear-logs');
  if (btnConfirmClearLogs) btnConfirmClearLogs.addEventListener('click', confirmClearLogs);

  // ==========================================================================
  // Task 9.5: Monitoring & Continuous Sync Event Listeners
  // ==========================================================================

  // Subtab navigation in Monitoring panel
  document.querySelectorAll('#tab-monitoring .subtab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const subtab = btn.getAttribute('data-subtab');
      if (subtab) switchMonitoringSubTab(subtab);
    });
  });

  // Top header actions
  const btnMonitoringLive = document.getElementById('btn-monitoring-live-toggle');
  if (btnMonitoringLive) {
    btnMonitoringLive.addEventListener('click', toggleMonitoringLivePolling);
  }

  const btnRefreshMonitoring = document.getElementById('btn-refresh-monitoring');
  if (btnRefreshMonitoring) {
    btnRefreshMonitoring.addEventListener('click', async () => {
      await fetchMonitoringData();
      showToast('Monitoring status refreshed', 'success', 1500);
    });
  }

  const btnSimulateWh = document.getElementById('btn-simulate-webhook');
  if (btnSimulateWh) {
    btnSimulateWh.addEventListener('click', simulateWebhookEvent);
  }

  const btnOpenPollModal = document.getElementById('btn-open-poll-modal');
  if (btnOpenPollModal) {
    btnOpenPollModal.addEventListener('click', openPollTriggerModal);
  }

  // Real-Time Sync Worker Controls
  const btnRtStart = document.getElementById('btn-rt-start');
  if (btnRtStart) btnRtStart.addEventListener('click', startRealtimeWorker);

  const btnRtPause = document.getElementById('btn-rt-pause');
  if (btnRtPause) btnRtPause.addEventListener('click', pauseRealtimeWorker);

  const btnRtStop = document.getElementById('btn-rt-stop');
  if (btnRtStop) btnRtStop.addEventListener('click', stopRealtimeWorker);

  const btnRtReset = document.getElementById('btn-rt-reset');
  if (btnRtReset) btnRtReset.addEventListener('click', resetRealtimeWorkerStats);

  // Fallback Polling Worker Controls
  const btnPollStart = document.getElementById('btn-poll-start');
  if (btnPollStart) btnPollStart.addEventListener('click', startPollingWorker);

  const btnPollPause = document.getElementById('btn-poll-pause');
  if (btnPollPause) btnPollPause.addEventListener('click', pausePollingWorker);

  const btnPollStop = document.getElementById('btn-poll-stop');
  if (btnPollStop) btnPollStop.addEventListener('click', stopPollingWorker);

  const btnPollQuickTrigger = document.getElementById('btn-poll-quick-trigger');
  if (btnPollQuickTrigger) btnPollQuickTrigger.addEventListener('click', openPollTriggerModal);

  const btnTriggerPollNow = document.getElementById('btn-trigger-poll-now');
  if (btnTriggerPollNow) btnTriggerPollNow.addEventListener('click', openPollTriggerModal);

  const btnPollRefreshCheckpoints = document.getElementById('btn-poll-refresh-checkpoints');
  if (btnPollRefreshCheckpoints) btnPollRefreshCheckpoints.addEventListener('click', fetchMonitoringData);

  // Webhook Receiver & Queue Card Controls
  const btnWhReset = document.getElementById('btn-wh-reset-stats');
  if (btnWhReset) btnWhReset.addEventListener('click', resetWebhookStats);

  const btnWhClear = document.getElementById('btn-wh-clear-events');
  if (btnWhClear) btnWhClear.addEventListener('click', clearWebhookEventsFeed);

  const btnSubtabSimulateWh = document.getElementById('btn-subtab-simulate-wh');
  if (btnSubtabSimulateWh) btnSubtabSimulateWh.addEventListener('click', simulateWebhookEvent);

  const btnEmptySimulateWh = document.getElementById('btn-empty-simulate-wh');
  if (btnEmptySimulateWh) btnEmptySimulateWh.addEventListener('click', simulateWebhookEvent);

  const btnClearWhHistory = document.getElementById('btn-clear-wh-history');
  if (btnClearWhHistory) btnClearWhHistory.addEventListener('click', clearWebhookEventsFeed);

  const btnQueueFlushQuick = document.getElementById('btn-queue-flush-quick');
  if (btnQueueFlushQuick) btnQueueFlushQuick.addEventListener('click', flushWebhookQueue);

  const btnQueueFlushNow = document.getElementById('btn-queue-flush-now');
  if (btnQueueFlushNow) btnQueueFlushNow.addEventListener('click', flushWebhookQueue);

  const btnQueuePauseQuick = document.getElementById('btn-queue-pause-toggle-quick');
  if (btnQueuePauseQuick) btnQueuePauseQuick.addEventListener('click', toggleQueuePause);

  const btnQueuePauseToggle = document.getElementById('btn-queue-pause-toggle');
  if (btnQueuePauseToggle) btnQueuePauseToggle.addEventListener('click', toggleQueuePause);

  const btnDlqRetryQuick = document.getElementById('btn-dlq-retry-quick');
  if (btnDlqRetryQuick) btnDlqRetryQuick.addEventListener('click', retryAllDlq);

  const btnDlqRetryAll = document.getElementById('btn-dlq-retry-all');
  if (btnDlqRetryAll) btnDlqRetryAll.addEventListener('click', retryAllDlq);

  const btnQueueClearBuffer = document.getElementById('btn-queue-clear-buffer');
  if (btnQueueClearBuffer) btnQueueClearBuffer.addEventListener('click', clearWebhookQueueBuffer);

  const btnDlqClearAll = document.getElementById('btn-dlq-clear-all');
  if (btnDlqClearAll) btnDlqClearAll.addEventListener('click', () => clearDlq());

  // Webhook Events Search & Filter
  const whSearchInput = document.getElementById('wh-search-input') as HTMLInputElement | null;
  if (whSearchInput) {
    whSearchInput.addEventListener('input', (e) => {
      state.monitoring.webhooks.search = (e.target as HTMLInputElement).value;
      renderWebhookEvents();
    });
  }

  const whFilterType = document.getElementById('wh-filter-type') as HTMLSelectElement | null;
  if (whFilterType) {
    whFilterType.addEventListener('change', (e) => {
      state.monitoring.webhooks.filterType = (e.target as HTMLSelectElement).value;
      renderWebhookEvents();
    });
  }

  const whFilterAction = document.getElementById('wh-filter-action') as HTMLSelectElement | null;
  if (whFilterAction) {
    whFilterAction.addEventListener('change', (e) => {
      state.monitoring.webhooks.filterAction = (e.target as HTMLSelectElement).value;
      renderWebhookEvents();
    });
  }

  const btnResetWhFilters = document.getElementById('btn-reset-wh-filters');
  if (btnResetWhFilters) {
    btnResetWhFilters.addEventListener('click', () => {
      state.monitoring.webhooks.search = '';
      state.monitoring.webhooks.filterType = '';
      state.monitoring.webhooks.filterAction = '';
      if (whSearchInput) whSearchInput.value = '';
      if (whFilterType) whFilterType.value = '';
      if (whFilterAction) whFilterAction.value = '';
      renderWebhookEvents();
    });
  }

  // Checkpoints global actions
  const btnSetCheckpointsNow = document.getElementById('btn-set-all-checkpoints-now');
  if (btnSetCheckpointsNow) btnSetCheckpointsNow.addEventListener('click', () => setAllCheckpoints(0));

  const btnSetCheckpoints1h = document.getElementById('btn-set-all-checkpoints-1h');
  if (btnSetCheckpoints1h) btnSetCheckpoints1h.addEventListener('click', () => setAllCheckpoints(3600000));

  const btnResetAllCp = document.getElementById('btn-reset-all-checkpoints');
  if (btnResetAllCp) btnResetAllCp.addEventListener('click', resetAllCheckpoints);

  // Modals event listeners
  const btnCloseEventModal = document.getElementById('btn-close-event-modal');
  if (btnCloseEventModal) btnCloseEventModal.addEventListener('click', closeEventDetailModal);

  const btnDismissEventModal = document.getElementById('btn-dismiss-event-modal');
  if (btnDismissEventModal) btnDismissEventModal.addEventListener('click', closeEventDetailModal);

  const btnClosePollModal = document.getElementById('btn-close-poll-modal');
  if (btnClosePollModal) btnClosePollModal.addEventListener('click', closePollTriggerModal);

  const btnCancelPollModal = document.getElementById('btn-cancel-poll-modal');
  if (btnCancelPollModal) btnCancelPollModal.addEventListener('click', closePollTriggerModal);

  const btnConfirmPollTrigger = document.getElementById('btn-confirm-poll-trigger');
  if (btnConfirmPollTrigger) btnConfirmPollTrigger.addEventListener('click', confirmPollTrigger);

  const btnCloseCpModal = document.getElementById('btn-close-checkpoint-modal');
  if (btnCloseCpModal) btnCloseCpModal.addEventListener('click', closeCheckpointEditModal);

  const btnCancelCpModal = document.getElementById('btn-cancel-checkpoint-modal');
  if (btnCancelCpModal) btnCancelCpModal.addEventListener('click', closeCheckpointEditModal);

  const btnSaveCp = document.getElementById('btn-save-checkpoint');
  if (btnSaveCp) btnSaveCp.addEventListener('click', saveCheckpointFromModal);

  const btnCpSetNow = document.getElementById('btn-cp-set-now');
  if (btnCpSetNow) {
    btnCpSetNow.addEventListener('click', () => {
      const input = document.getElementById('edit-checkpoint-timestamp') as HTMLInputElement | null;
      if (input) input.value = new Date().toISOString();
    });
  }

  const btnCpSet1h = document.getElementById('btn-cp-set-1h');
  if (btnCpSet1h) {
    btnCpSet1h.addEventListener('click', () => {
      const input = document.getElementById('edit-checkpoint-timestamp') as HTMLInputElement | null;
      if (input) input.value = new Date(Date.now() - 3600000).toISOString();
    });
  }

  const btnCpSet24h = document.getElementById('btn-cp-set-24h');
  if (btnCpSet24h) {
    btnCpSet24h.addEventListener('click', () => {
      const input = document.getElementById('edit-checkpoint-timestamp') as HTMLInputElement | null;
      if (input) input.value = new Date(Date.now() - 86400000).toISOString();
    });
  }

  // On-demand sync form listener
  const ondemandForm = document.getElementById('ondemand-sync-form');
  if (ondemandForm) {
    ondemandForm.addEventListener('submit', executeOnDemandSync);
  }

  // ==========================================
  // Task 9.3: Batch Sync & Catalog Event Listeners
  // ==========================================
  // Batch subtab navigation
  document.querySelectorAll('#batch-view-container .subtab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const subtab = btn.getAttribute('data-subtab');
      if (subtab && (subtab === 'catalog' || subtab === 'orchestrator' || subtab === 'diffs')) {
        switchBatchSubTab(subtab);
      }
    });
  });

  // Header Actions
  const btnRefreshBatch = document.getElementById('btn-refresh-batch');
  if (btnRefreshBatch) {
    btnRefreshBatch.addEventListener('click', async () => {
      await fetchBatchInitialData();
      showToast('Catalog and discovery data refreshed', 'success', 1500);
    });
  }

  const btnRunValidation = document.getElementById('btn-run-validation');
  if (btnRunValidation) {
    btnRunValidation.addEventListener('click', runPreFlightValidation);
  }

  const btnRunDryRun = document.getElementById('btn-run-dry-run');
  if (btnRunDryRun) {
    btnRunDryRun.addEventListener('click', runDryRunDiffPreview);
  }

  const btnRunBatch = document.getElementById('btn-run-batch-sync');
  if (btnRunBatch) {
    btnRunBatch.addEventListener('click', () => executeBatchSyncJob(false));
  }

  // Catalog Browser Controls
  const treeSearch = document.getElementById('category-tree-search') as HTMLInputElement | null;
  if (treeSearch) {
    treeSearch.addEventListener('input', (e) => {
      state.batch.catalog.categorySearch = (e.target as HTMLInputElement).value;
      renderCategoryTree();
    });
  }

  const btnExpandTree = document.getElementById('btn-expand-tree');
  if (btnExpandTree) {
    btnExpandTree.addEventListener('click', () => {
      function expandAll(nodes: any[]) {
        if (!Array.isArray(nodes)) return;
        nodes.forEach((n) => {
          state.batch.catalog.expandedNodes.add(n.code);
          if (n.children) expandAll(n.children);
        });
      }
      expandAll(state.batch.catalog.categoryTree);
      renderCategoryTree();
    });
  }

  const btnCollapseTree = document.getElementById('btn-collapse-tree');
  if (btnCollapseTree) {
    btnCollapseTree.addEventListener('click', () => {
      state.batch.catalog.expandedNodes.clear();
      renderCategoryTree();
    });
  }

  const treeClearBtn = document.getElementById('tree-clear-selection');
  if (treeClearBtn) {
    treeClearBtn.addEventListener('click', () => selectCategoryFilter(null));
  }

  // Filter Dropdowns
  const famFilter = document.getElementById('catalog-family-filter') as HTMLSelectElement | null;
  if (famFilter) {
    famFilter.addEventListener('change', () => {
      state.batch.catalog.page = 1;
      fetchCatalogItems();
    });
  }

  const chanFilter = document.getElementById('catalog-channel-filter') as HTMLSelectElement | null;
  if (chanFilter) {
    chanFilter.addEventListener('change', () => {
      state.batch.catalog.page = 1;
      fetchCatalogItems();
    });
  }

  const statFilter = document.getElementById('catalog-status-filter') as HTMLSelectElement | null;
  if (statFilter) {
    statFilter.addEventListener('change', () => {
      state.batch.catalog.page = 1;
      fetchCatalogItems();
    });
  }

  const compFilter = document.getElementById('catalog-completeness-filter') as HTMLSelectElement | null;
  if (compFilter) {
    compFilter.addEventListener('change', () => {
      state.batch.catalog.page = 1;
      fetchCatalogItems();
    });
  }

  const updatedFilter = document.getElementById('catalog-updated-filter') as HTMLInputElement | null;
  if (updatedFilter) {
    updatedFilter.addEventListener('change', () => {
      state.batch.catalog.page = 1;
      fetchCatalogItems();
    });
  }

  const btnResetCatalogFilters = document.getElementById('btn-reset-catalog-filters');
  if (btnResetCatalogFilters) {
    btnResetCatalogFilters.addEventListener('click', () => {
      if (famFilter) famFilter.value = '';
      if (chanFilter) chanFilter.value = '';
      if (statFilter) statFilter.value = '';
      if (compFilter) compFilter.value = '';
      if (updatedFilter) updatedFilter.value = '';
      const sInput = document.getElementById('catalog-search-input') as HTMLInputElement | null;
      if (sInput) sInput.value = '';
      state.batch.catalog.selectedCategory = null;
      state.batch.catalog.page = 1;
      selectCategoryFilter(null);
    });
  }

  // Entity Type Toggle (Products vs Models)
  const toggleProducts = document.getElementById('catalog-entity-toggle-products');
  const toggleModels = document.getElementById('catalog-entity-toggle-models');
  if (toggleProducts && toggleModels) {
    toggleProducts.addEventListener('click', () => {
      if (state.batch.catalog.entityType === 'products') return;
      state.batch.catalog.entityType = 'products';
      toggleProducts.classList.add('active');
      toggleModels.classList.remove('active');
      state.batch.catalog.page = 1;
      state.batch.catalog.selectedItemIds.clear();
      fetchCatalogItems();
    });
    toggleModels.addEventListener('click', () => {
      if (state.batch.catalog.entityType === 'product_models') return;
      state.batch.catalog.entityType = 'product_models';
      toggleModels.classList.add('active');
      toggleProducts.classList.remove('active');
      state.batch.catalog.page = 1;
      state.batch.catalog.selectedItemIds.clear();
      fetchCatalogItems();
    });
  }

  // Search input & button
  const catalogSearchInput = document.getElementById('catalog-search-input') as HTMLInputElement | null;
  const btnSearch = document.getElementById('btn-catalog-search');
  if (catalogSearchInput) {
    catalogSearchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        state.batch.catalog.page = 1;
        fetchCatalogItems();
      }
    });
  }
  if (btnSearch) {
    btnSearch.addEventListener('click', () => {
      state.batch.catalog.page = 1;
      fetchCatalogItems();
    });
  }

  // Select all checkbox
  const selectAllCb = document.getElementById('catalog-select-all-cb') as HTMLInputElement | null;
  if (selectAllCb) {
    selectAllCb.addEventListener('change', (e) => {
      selectAllCatalogItems((e.target as HTMLInputElement).checked);
    });
  }

  // Selective action buttons
  const btnSelectiveDryRun = document.getElementById('btn-selective-dryrun');
  if (btnSelectiveDryRun) btnSelectiveDryRun.addEventListener('click', executeSelectiveDryRun);

  const btnSelectiveSync = document.getElementById('btn-selective-sync');
  if (btnSelectiveSync) btnSelectiveSync.addEventListener('click', executeSelectiveSync);

  const btnClearSelection = document.getElementById('btn-clear-selection');
  if (btnClearSelection) btnClearSelection.addEventListener('click', clearCatalogSelection);

  // Pagination
  const pageSizeSelect = document.getElementById('catalog-page-size') as HTMLSelectElement | null;
  if (pageSizeSelect) {
    pageSizeSelect.addEventListener('change', (e) => {
      state.batch.catalog.limit = Number((e.target as HTMLSelectElement).value) || 25;
      state.batch.catalog.page = 1;
      fetchCatalogItems();
    });
  }

  const btnPrev = document.getElementById('catalog-btn-prev') as HTMLButtonElement | null;
  if (btnPrev) {
    btnPrev.addEventListener('click', () => {
      if (state.batch.catalog.page > 1) {
        state.batch.catalog.page--;
        fetchCatalogItems();
      }
    });
  }

  const btnNext = document.getElementById('catalog-btn-next') as HTMLButtonElement | null;
  if (btnNext) {
    btnNext.addEventListener('click', () => {
      state.batch.catalog.page++;
      fetchCatalogItems();
    });
  }

  const btnEmptyReset = document.getElementById('btn-catalog-empty-reset');
  if (btnEmptyReset) {
    btnEmptyReset.addEventListener('click', () => {
      if (btnResetCatalogFilters) btnResetCatalogFilters.click();
    });
  }

  // Orchestrator Presets
  const btnPresetAll = document.getElementById('btn-preset-all');
  if (btnPresetAll) btnPresetAll.addEventListener('click', () => setEntityTypePreset('all'));

  const btnPresetCatalog = document.getElementById('btn-preset-catalog');
  if (btnPresetCatalog) btnPresetCatalog.addEventListener('click', () => setEntityTypePreset('catalog'));

  const btnPresetStructure = document.getElementById('btn-preset-structure');
  if (btnPresetStructure) btnPresetStructure.addEventListener('click', () => setEntityTypePreset('structure'));

  const btnPresetConfig = document.getElementById('btn-preset-config');
  if (btnPresetConfig) btnPresetConfig.addEventListener('click', () => setEntityTypePreset('config'));

  const btnPresetNone = document.getElementById('btn-preset-none');
  if (btnPresetNone) btnPresetNone.addEventListener('click', () => setEntityTypePreset('none'));

  // Orchestrator Modes
  const modeDryrun = document.getElementById('mode-dryrun');
  const modeLive = document.getElementById('mode-live');
  const labelModeDryrun = document.getElementById('label-mode-dryrun');
  const labelModeLive = document.getElementById('label-mode-live');

  if (modeDryrun && modeLive) {
    modeDryrun.addEventListener('change', () => {
      if (labelModeDryrun) labelModeDryrun.classList.add('active');
      if (labelModeLive) labelModeLive.classList.remove('active');
      state.batch.orchestrator.mode = 'dryrun';
    });
    modeLive.addEventListener('change', () => {
      if (labelModeLive) labelModeLive.classList.add('active');
      if (labelModeDryrun) labelModeDryrun.classList.remove('active');
      state.batch.orchestrator.mode = 'live';
    });
  }

  // Orchestrator Action Buttons
  const btnExecValidate = document.getElementById('btn-execute-validate');
  if (btnExecValidate) btnExecValidate.addEventListener('click', runPreFlightValidation);

  const btnExecDryRun = document.getElementById('btn-execute-dryrun');
  if (btnExecDryRun) btnExecDryRun.addEventListener('click', runDryRunDiffPreview);

  const btnExecBatch = document.getElementById('btn-execute-batch');
  if (btnExecBatch) {
    btnExecBatch.addEventListener('click', () => {
      const isDryRun = state.batch.orchestrator.mode === 'dryrun';
      executeBatchSyncJob(isDryRun);
    });
  }

  // Task 6.2: Mirror / prune deleted items controls
  const optPruneDeleted = document.getElementById('opt-prune-deleted') as HTMLInputElement | null;
  if (optPruneDeleted) {
    optPruneDeleted.addEventListener('change', () => {
      state.batch.orchestrator.pruneEnabled = optPruneDeleted.checked;
    });
  }

  const optForceDelete = document.getElementById('opt-force-delete') as HTMLInputElement | null;
  if (optForceDelete) {
    optForceDelete.addEventListener('change', () => {
      state.batch.orchestrator.forceDelete = optForceDelete.checked;
    });
  }

  const optPruneThreshold = document.getElementById('opt-prune-threshold') as HTMLInputElement | null;
  if (optPruneThreshold) {
    optPruneThreshold.addEventListener('change', () => {
      const value = Number(optPruneThreshold.value);
      state.batch.orchestrator.pruneThreshold = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 15;
    });
  }

  // Diffs Filter Toolbar
  const diffFilterEntity = document.getElementById('diff-filter-entity');
  if (diffFilterEntity) diffFilterEntity.addEventListener('change', filterDiffItems);

  const diffFilterAction = document.getElementById('diff-filter-action');
  if (diffFilterAction) diffFilterAction.addEventListener('change', filterDiffItems);

  const diffSearch = document.getElementById('diff-search-input');
  if (diffSearch) diffSearch.addEventListener('input', filterDiffItems);

  const btnExpandAllDiffs = document.getElementById('btn-expand-all-diffs');
  if (btnExpandAllDiffs) {
    btnExpandAllDiffs.addEventListener('click', () => {
      (state.batch.validation.diffItems || []).forEach((item: any) => {
        state.batch.validation.expandedDiffIds.add(`${item.entityType}:${item.id}`);
      });
      filterDiffItems();
    });
  }

  const btnCollapseAllDiffs = document.getElementById('btn-collapse-all-diffs');
  if (btnCollapseAllDiffs) {
    btnCollapseAllDiffs.addEventListener('click', () => {
      state.batch.validation.expandedDiffIds.clear();
      filterDiffItems();
    });
  }

  const btnLaunchFromDiff = document.getElementById('btn-launch-from-diff');
  if (btnLaunchFromDiff) {
    btnLaunchFromDiff.addEventListener('click', () => {
      executeBatchSyncJob(false);
    });
  }

  // ==========================================================================
  // Task 14.1: Source Entity Explorer Navigation & Multi-Entity Browser UI
  // ==========================================================================
  const explorerEntitySelect = document.getElementById('explorer-entity-type') as HTMLSelectElement | null;
  if (explorerEntitySelect) {
    explorerEntitySelect.addEventListener('change', () => {
      setExplorerEntityType(explorerEntitySelect.value);
    });
  }

  const explorerSearchInput = document.getElementById('explorer-search-input') as HTMLInputElement | null;
  if (explorerSearchInput) {
    explorerSearchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        setExplorerSearch(explorerSearchInput.value);
      }
    });
  }

  const btnExplorerSearch = document.getElementById('btn-explorer-search');
  if (btnExplorerSearch) {
    btnExplorerSearch.addEventListener('click', () => {
      const input = document.getElementById('explorer-search-input') as HTMLInputElement | null;
      if (input) setExplorerSearch(input.value);
    });
  }

  const explorerEnabledFilter = document.getElementById('explorer-enabled-filter') as HTMLSelectElement | null;
  if (explorerEnabledFilter) {
    explorerEnabledFilter.addEventListener('change', () => {
      setExplorerEnabledFilter(explorerEnabledFilter.value as '' | 'true' | 'false');
    });
  }

  const btnExplorerReset = document.getElementById('btn-explorer-reset');
  if (btnExplorerReset) {
    btnExplorerReset.addEventListener('click', resetExplorerFilters);
  }

  const btnExplorerRefresh = document.getElementById('btn-explorer-refresh');
  if (btnExplorerRefresh) {
    btnExplorerRefresh.addEventListener('click', async () => {
      await fetchExplorerItems();
      showToast('Explorer records refreshed', 'success', 1500);
    });
  }

  const explorerPageSize = document.getElementById('explorer-page-size') as HTMLSelectElement | null;
  if (explorerPageSize) {
    explorerPageSize.addEventListener('change', () => {
      setExplorerPageSize(Number(explorerPageSize.value) || 25);
    });
  }

  const btnExplorerPrev = document.getElementById('explorer-btn-prev') as HTMLButtonElement | null;
  if (btnExplorerPrev) {
    btnExplorerPrev.addEventListener('click', () => {
      if (state.explorer.page > 1) {
        setExplorerPage(state.explorer.page - 1);
      }
    });
  }

  const btnExplorerNext = document.getElementById('explorer-btn-next') as HTMLButtonElement | null;
  if (btnExplorerNext) {
    btnExplorerNext.addEventListener('click', () => {
      setExplorerPage(state.explorer.page + 1);
    });
  }

  const btnExplorerEmptyReset = document.getElementById('btn-explorer-empty-reset');
  if (btnExplorerEmptyReset) {
    btnExplorerEmptyReset.addEventListener('click', resetExplorerFilters);
  }

  // Task 6.2: Explorer deletion controls
  const explorerSelectAllCb = document.getElementById('explorer-select-all-cb') as HTMLInputElement | null;
  if (explorerSelectAllCb) {
    explorerSelectAllCb.addEventListener('change', () => {
      selectAllExplorerItems(explorerSelectAllCb.checked);
    });
  }

  const btnExplorerBatchDelete = document.getElementById('btn-explorer-batch-delete');
  if (btnExplorerBatchDelete) {
    btnExplorerBatchDelete.addEventListener('click', openBatchDeleteModal);
  }

  const btnExplorerClearSelection = document.getElementById('btn-explorer-clear-selection');
  if (btnExplorerClearSelection) {
    btnExplorerClearSelection.addEventListener('click', clearExplorerSelection);
  }

  const btnCloseExplorerBatchDelete = document.getElementById('btn-close-explorer-batch-delete');
  const btnCancelExplorerBatchDelete = document.getElementById('btn-cancel-explorer-batch-delete');
  const btnConfirmExplorerBatchDelete = document.getElementById('btn-confirm-explorer-batch-delete') as HTMLButtonElement | null;
  const cbExplorerBatchDeleteConfirm = document.getElementById('explorer-batch-delete-confirm-cb') as HTMLInputElement | null;

  if (btnCloseExplorerBatchDelete) btnCloseExplorerBatchDelete.addEventListener('click', closeBatchDeleteModal);
  if (btnCancelExplorerBatchDelete) btnCancelExplorerBatchDelete.addEventListener('click', closeBatchDeleteModal);
  if (btnConfirmExplorerBatchDelete) {
    btnConfirmExplorerBatchDelete.addEventListener('click', () => confirmBatchDelete());
  }
  if (cbExplorerBatchDeleteConfirm) {
    cbExplorerBatchDeleteConfirm.addEventListener('change', () => {
      if (btnConfirmExplorerBatchDelete) {
        btnConfirmExplorerBatchDelete.disabled = !cbExplorerBatchDeleteConfirm.checked;
      }
    });
  }

  // Task 16.4: Virtual Target UI Integration
  const btnExplorerBatchAddTarget = document.getElementById('btn-explorer-batch-add-target');
  if (btnExplorerBatchAddTarget) {
    btnExplorerBatchAddTarget.addEventListener('click', () => openAddToVirtualTargetModal());
  }

  const btnExplorerSelectionAddTarget = document.getElementById('btn-explorer-selection-add-target');
  if (btnExplorerSelectionAddTarget) {
    btnExplorerSelectionAddTarget.addEventListener('click', () => openAddToVirtualTargetModal());
  }

  const btnCloseExplorerAddTarget = document.getElementById('btn-close-explorer-add-target');
  const btnCancelExplorerAddTarget = document.getElementById('btn-cancel-explorer-add-target');
  const btnConfirmExplorerAddTarget = document.getElementById('btn-confirm-explorer-add-target');

  if (btnCloseExplorerAddTarget) btnCloseExplorerAddTarget.addEventListener('click', closeAddToVirtualTargetModal);
  if (btnCancelExplorerAddTarget) btnCancelExplorerAddTarget.addEventListener('click', closeAddToVirtualTargetModal);
  if (btnConfirmExplorerAddTarget) {
    btnConfirmExplorerAddTarget.addEventListener('click', () => confirmAddToVirtualTarget());
  }

  const targetModeExistingRadio = document.getElementById('explorer-target-mode-existing');
  const targetModeNewRadio = document.getElementById('explorer-target-mode-new');
  if (targetModeExistingRadio) {
    targetModeExistingRadio.addEventListener('change', () => setExplorerTargetMode('existing'));
  }
  if (targetModeNewRadio) {
    targetModeNewRadio.addEventListener('change', () => setExplorerTargetMode('new'));
  }

  // Modals close buttons
  const btnCloseInspect = document.getElementById('btn-close-entity-inspect');
  const btnDismissInspect = document.getElementById('btn-dismiss-entity-inspect');
  if (btnCloseInspect) btnCloseInspect.addEventListener('click', () => {
    const modal = document.getElementById('entity-inspect-modal');
    if (modal) modal.style.display = 'none';
  });
  if (btnDismissInspect) btnDismissInspect.addEventListener('click', () => {
    const modal = document.getElementById('entity-inspect-modal');
    if (modal) modal.style.display = 'none';
  });

  const btnCopyJson = document.getElementById('btn-copy-entity-json');
  if (btnCopyJson) {
    btnCopyJson.addEventListener('click', () => {
      if (state.batch.inspectEntity?.payload) {
        navigator.clipboard.writeText(JSON.stringify(state.batch.inspectEntity.payload, null, 2));
        showToast('JSON copied to clipboard', 'success', 1500);
      }
    });
  }

  const btnSyncSingle = document.getElementById('btn-sync-single-entity');
  if (btnSyncSingle) {
    btnSyncSingle.addEventListener('click', () => {
      if (state.batch.inspectEntity) {
        const modal = document.getElementById('entity-inspect-modal');
        if (modal) modal.style.display = 'none';
        executeBatchSyncJob(false, {
          items: [state.batch.inspectEntity.entityId],
          entity_types: [state.batch.inspectEntity.entityType],
        });
      }
    });
  }

  const btnCascadeInspect = document.getElementById('btn-cascade-sync-inspect');
  if (btnCascadeInspect) {
    btnCascadeInspect.addEventListener('click', () => {
      if (state.batch.inspectEntity) {
        const modal = document.getElementById('entity-inspect-modal');
        if (modal) modal.style.display = 'none';
        const selected = getSelectedTreeItems('inspect');
        executeEntityCascadeSync(
          state.batch.inspectEntity.entityType,
          state.batch.inspectEntity.entityId,
          activeInspectStrategy,
          selected
        );
      }
    });
  }

  // Task 14.4: Side-by-Side Source vs Target Entity Diff & Instant Sync Action
  const btnCloseDiff = document.getElementById('btn-close-entity-diff');
  const btnDismissDiff = document.getElementById('btn-dismiss-entity-diff');
  if (btnCloseDiff) {
    btnCloseDiff.addEventListener('click', () => {
      if (state.comparison.diffModal) {
        closeComparisonDiffModal();
      } else {
        closeEntityDiffModal();
      }
    });
  }
  if (btnDismissDiff) {
    btnDismissDiff.addEventListener('click', () => {
      if (state.comparison.diffModal) {
        closeComparisonDiffModal();
      } else {
        closeEntityDiffModal();
      }
    });
  }

  const btnDiffSyncNow = document.getElementById('btn-entity-diff-sync-now');
  if (btnDiffSyncNow) btnDiffSyncNow.addEventListener('click', executeEntityDiffSync);

  const btnCascadeDiff = document.getElementById('btn-cascade-sync-diff');
  if (btnCascadeDiff) {
    btnCascadeDiff.addEventListener('click', () => {
      if (state.explorer.diffModal) {
        const modal = document.getElementById('entity-diff-modal');
        if (modal) modal.style.display = 'none';
        const selected = getSelectedTreeItems('diff');
        executeEntityCascadeSync(
          state.explorer.diffModal.entityType,
          state.explorer.diffModal.entityId,
          activeDiffStrategy,
          selected
        );
      }
    });
  }

  // Frontend: interactive drill-down diff detail (comparison mode)
  const btnComparisonDiffSyncRight = document.getElementById('btn-comparison-diff-sync-right');
  if (btnComparisonDiffSyncRight) {
    btnComparisonDiffSyncRight.addEventListener('click', executeComparisonDiffSync);
  }

  const btnComparisonDiffAddToPackage = document.getElementById('btn-comparison-diff-add-to-package');
  if (btnComparisonDiffAddToPackage) {
    btnComparisonDiffAddToPackage.addEventListener('click', openComparisonEntityAddToPackage);
  }

  const btnCloseBatchModal = document.getElementById('btn-close-batch-progress');
  const btnDismissBatchModal = document.getElementById('btn-dismiss-batch-modal');
  if (btnCloseBatchModal) btnCloseBatchModal.addEventListener('click', () => {
    const modal = document.getElementById('batch-progress-modal');
    if (modal) modal.style.display = 'none';
    if (state.batch.progressPollTimer) {
      clearInterval(state.batch.progressPollTimer);
      state.batch.progressPollTimer = null;
    }
  });
  if (btnDismissBatchModal) btnDismissBatchModal.addEventListener('click', () => {
    const modal = document.getElementById('batch-progress-modal');
    if (modal) modal.style.display = 'none';
    if (state.batch.progressPollTimer) {
      clearInterval(state.batch.progressPollTimer);
      state.batch.progressPollTimer = null;
    }
  });

  const btnBatchViewLogs = document.getElementById('btn-batch-view-in-logs');
  if (btnBatchViewLogs) {
    btnBatchViewLogs.addEventListener('click', () => {
      const modal = document.getElementById('batch-progress-modal');
      if (modal) modal.style.display = 'none';
      switchTab('logs');
    });
  }

  document.querySelectorAll('.modal-backdrop').forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeOAuthModal();
        closeDeleteModal();
        closeJobDetailModal();
        closeClearLogsModal();
        closeEventDetailModal();
        closePollTriggerModal();
        closeCheckpointEditModal();
        closeBatchDeleteModal();
        closeStagingItemInspectModal();
        closeStagingBulkDeleteModal();
        const inspectModal = document.getElementById('entity-inspect-modal');
        if (inspectModal) inspectModal.style.display = 'none';
        const entityDiffModal = document.getElementById('entity-diff-modal');
        if (entityDiffModal) {
          if (state.comparison.diffModal) {
            closeComparisonDiffModal();
          } else {
            entityDiffModal.style.display = 'none';
            state.explorer.diffModal = null;
          }
        }
        const batchProgressModal = document.getElementById('batch-progress-modal');
        if (batchProgressModal) batchProgressModal.style.display = 'none';
      }
    });
  });

  // ==========================================================================
  // Task: Frontend comparison report & summary view event listeners
  // ==========================================================================
  const comparisonForm = document.getElementById('comparison-form');
  if (comparisonForm) {
    comparisonForm.addEventListener('submit', (e) => {
      e.preventDefault();
      runComparison();
    });
  }

  const comparisonLeftSide = document.getElementById('comparison-left-side') as HTMLSelectElement | null;
  const comparisonRightSide = document.getElementById('comparison-right-side') as HTMLSelectElement | null;
  if (comparisonLeftSide) {
    comparisonLeftSide.addEventListener('change', () => setComparisonSide('left', comparisonLeftSide.value));
  }
  if (comparisonRightSide) {
    comparisonRightSide.addEventListener('change', () => setComparisonSide('right', comparisonRightSide.value));
  }

  const comparisonEntityTypesGrid = document.getElementById('comparison-entity-types-grid');
  if (comparisonEntityTypesGrid) {
    comparisonEntityTypesGrid.addEventListener('change', (e) => {
      const target = e.target as HTMLElement;
      const cb = target.closest('.comparison-entity-type-cb') as HTMLInputElement | null;
      if (cb) {
        const card = cb.closest('.comparison-entity-type-card');
        card?.classList.toggle('active', cb.checked);
        toggleComparisonEntityType(cb.value, cb.checked);
      }
    });
  }

  const btnSelectAllComparisonTypes = document.getElementById('btn-comparison-select-all-types');
  const btnClearComparisonTypes = document.getElementById('btn-comparison-clear-types');
  if (btnSelectAllComparisonTypes) {
    btnSelectAllComparisonTypes.addEventListener('click', () => selectAllComparisonEntityTypes(true));
  }
  if (btnClearComparisonTypes) {
    btnClearComparisonTypes.addEventListener('click', () => selectAllComparisonEntityTypes(false));
  }

  const comparisonReportsList = document.getElementById('comparison-reports-list');
  if (comparisonReportsList) {
    comparisonReportsList.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const viewBtn = target.closest('.btn-view-report') as HTMLElement | null;
      const deleteBtn = target.closest('.btn-delete-report') as HTMLElement | null;
      const card = target.closest('.comparison-report-card') as HTMLElement | null;

      if (viewBtn) {
        const id = viewBtn.getAttribute('data-id');
        if (id) loadComparisonReport(id);
      } else if (deleteBtn) {
        const id = deleteBtn.getAttribute('data-id');
        if (id) deleteComparisonReport(id);
      } else if (card && !target.closest('button')) {
        const id = card.getAttribute('data-id');
        if (id) loadComparisonReport(id);
      }
    });
  }

  const comparisonTypeCards = document.getElementById('comparison-type-cards');
  if (comparisonTypeCards) {
    comparisonTypeCards.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const count = target.closest('.comparison-type-count') as HTMLElement | null;
      const card = target.closest('.comparison-type-card') as HTMLElement | null;
      if (count) {
        const entityType = count.getAttribute('data-entity-type') || card?.getAttribute('data-entity-type') || '';
        const changeType = count.getAttribute('data-change-type') || '';
        if (entityType && changeType && isValidComparisonEntityTypeChangeFilter(changeType)) {
          setComparisonEntityTypeChangeFilter(entityType, changeType as any);
          if (!state.comparison.expandedTypes.has(entityType)) {
            toggleComparisonTypeExpansion(entityType);
          }
        }
        return;
      }
      if (card) {
        const entityType = card.getAttribute('data-entity-type');
        if (entityType) toggleComparisonTypeExpansion(entityType);
      }
    });
  }

  const comparisonReportFilter = document.getElementById('comparison-report-filter');
  if (comparisonReportFilter) {
    comparisonReportFilter.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-report-filter]') as HTMLElement | null;
      if (btn) {
        const filter = btn.getAttribute('data-report-filter') || 'all';
        setComparisonReportFilter(filter as any);
      }
    });
  }

  const comparisonTypeEntitiesList = document.getElementById('comparison-type-entities-list');
  if (comparisonTypeEntitiesList) {
    comparisonTypeEntitiesList.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const header = target.closest('.comparison-type-entities-header') as HTMLElement | null;
      if (header) {
        const group = header.closest('.comparison-type-entities-group') as HTMLElement | null;
        const entityType = group?.getAttribute('data-entity-type');
        if (entityType) toggleComparisonTypeExpansion(entityType);
        return;
      }

      const paginationBtn = target.closest('.comparison-pagination-btn') as HTMLElement | null;
      if (paginationBtn) {
        const group = paginationBtn.closest('.comparison-type-entities-group') as HTMLElement | null;
        const entityType = group?.getAttribute('data-entity-type') || paginationBtn.getAttribute('data-entity-type') || '';
        const action = paginationBtn.getAttribute('data-action');
        const current = state.comparison.entityTypeStates.get(entityType)?.page || 1;
        const totalPages =
          paginationBtn
            .closest('.comparison-pagination')
            ?.querySelectorAll('button[data-action="page"]').length || current;
        if (action === 'prev') {
          setComparisonEntityTypePage(entityType, Math.max(1, current - 1));
        } else if (action === 'next') {
          setComparisonEntityTypePage(entityType, Math.min(totalPages, current + 1));
        } else if (action === 'page') {
          const page = parseInt(paginationBtn.getAttribute('data-page') || '1', 10);
          if (!Number.isNaN(page)) setComparisonEntityTypePage(entityType, page);
        }
        return;
      }

      const diffBtn = target.closest('.btn-view-entity-diff') as HTMLElement | null;
      if (diffBtn) {
        const reportId = diffBtn.getAttribute('data-report-id') || '';
        const entityType = diffBtn.getAttribute('data-entity-type') || '';
        const entityId = diffBtn.getAttribute('data-entity-id') || '';
        openComparisonEntityDiff(reportId, entityType, entityId);
      }
    });

    comparisonTypeEntitiesList.addEventListener('input', (e) => {
      const target = e.target as HTMLElement;
      const input = target.closest('.comparison-entity-search-input') as HTMLInputElement | null;
      if (input) {
        const entityType = input.getAttribute('data-entity-type') || '';
        setComparisonEntityTypeFilter(entityType, input.value);
      }
    });

    comparisonTypeEntitiesList.addEventListener('change', (e) => {
      const target = e.target as HTMLElement;
      const pageSizeSelect = target.closest('.comparison-page-size') as HTMLSelectElement | null;
      if (pageSizeSelect) {
        const entityType = pageSizeSelect.getAttribute('data-entity-type') || '';
        const value = parseInt(pageSizeSelect.value, 10);
        if (!Number.isNaN(value)) setComparisonEntityTypePageSize(entityType, value);
        return;
      }

      const showUnchangedToggle = target.closest('.comparison-show-unchanged-toggle') as HTMLInputElement | null;
      if (showUnchangedToggle) {
        const entityType = showUnchangedToggle.getAttribute('data-entity-type') || '';
        setComparisonEntityTypeShowUnchanged(entityType, showUnchangedToggle.checked);
      }
    });
  }

  // Footer embed toggle
  const footerToggle = document.getElementById('footer-embed-toggle');
  if (footerToggle) {
    footerToggle.addEventListener('click', toggleEmbeddingMode);
    footerToggle.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleEmbeddingMode();
      }
    });
  }

  // Akeneo Iframe Parent Window message handling
  window.addEventListener('message', (event) => {
    if (!event.data || typeof event.data !== 'object') return;
    if (event.data.type === 'AKENEO_NAVIGATE' && event.data.tab) {
      switchTab(event.data.tab);
    }
  });
}
